import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/pages/Login";
import { Building2, Users } from "lucide-react";

export default function RegisterBusiness() {
  const { registerB2B } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("employer");
  const [form, setForm] = useState({
    name: "", email: "", password: "", org_name: "",
    website: "", country: "", org_number: "", vat_number: "",
  });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accept) { setError("Please accept the Terms of Service and User Rules to continue."); return; }
    setLoading(true);
    try {
      await registerB2B({ ...form, role, accept_terms: true });
      navigate("/plans");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const RoleTab = ({ value, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => setRole(value)}
      data-testid={`b2b-role-${value}`}
      className={`flex-1 flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
        role === value
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <AuthShell
      title="Create a business account"
      subtitle="Start a 14-day trial. Verify by email — company registry check is automatic where available, and never blocks you."
      footer={<>Looking for a job instead? <Link to="/register" className="font-semibold text-slate-900 underline">Candidate sign-up</Link></>}
    >
      <div className="mb-5 flex gap-2" data-testid="b2b-role-tabs">
        <RoleTab value="employer" icon={Building2} label="Employer" />
        <RoleTab value="recruiter" icon={Users} label="Recruiter" />
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="register-business-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="name">Your name</Label>
            <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)}
              data-testid="b2b-name-input" className="mt-1.5" placeholder="Jane Doe" />
          </div>
          <div>
            <Label htmlFor="org_name">Company name</Label>
            <Input id="org_name" required value={form.org_name} onChange={(e) => set("org_name", e.target.value)}
              data-testid="b2b-orgname-input" className="mt-1.5" placeholder="Acme AS" />
          </div>
        </div>
        <div>
          <Label htmlFor="email">Work email</Label>
          <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
            data-testid="b2b-email-input" className="mt-1.5" placeholder="you@company.com" />
          <p className="mt-1 text-xs text-slate-400">Using your company domain auto-verifies your domain.</p>
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required value={form.password} onChange={(e) => set("password", e.target.value)}
            data-testid="b2b-password-input" className="mt-1.5" placeholder="At least 8 characters" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="website">Company website <span className="text-slate-400">(optional)</span></Label>
            <Input id="website" value={form.website} onChange={(e) => set("website", e.target.value)}
              data-testid="b2b-website-input" className="mt-1.5" placeholder="company.com" />
          </div>
          <div>
            <Label htmlFor="country">Country <span className="text-slate-400">(optional)</span></Label>
            <Input id="country" value={form.country} onChange={(e) => set("country", e.target.value)}
              data-testid="b2b-country-input" className="mt-1.5" placeholder="NO, SE, DE…" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="org_number">Company reg. no. <span className="text-slate-400">(optional)</span></Label>
            <Input id="org_number" value={form.org_number} onChange={(e) => set("org_number", e.target.value)}
              data-testid="b2b-orgnumber-input" className="mt-1.5" placeholder="Norway: 9 digits" />
          </div>
          <div>
            <Label htmlFor="vat_number">EU VAT no. <span className="text-slate-400">(optional)</span></Label>
            <Input id="vat_number" value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)}
              data-testid="b2b-vat-input" className="mt-1.5" placeholder="e.g. DE123456789" />
          </div>
        </div>
        <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer" data-testid="b2b-accept-terms-label">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-slate-900" data-testid="b2b-accept-terms-checkbox" />
          <span>
            I agree to the <Link to="/terms" target="_blank" className="underline" data-testid="b2b-terms-link">Terms of Service</Link> and
            the <Link to="/rules" target="_blank" className="underline" data-testid="b2b-rules-link">User Rules</Link>, and I confirm the
            jobs and requirements we publish will be accurate. See the <Link to="/privacy" target="_blank" className="underline">Privacy Policy</Link>.
          </span>
        </label>
        {error && <p className="text-sm text-red-600" data-testid="b2b-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="b2b-submit-btn" className="w-full bg-slate-900 hover:bg-slate-800">
          {loading ? "Creating…" : "Create business account"}
        </Button>
        <p className="text-center text-xs text-slate-400">
          A confirmation email is sent to activate your account. Registry checks apply only where a public register exists — missing data never blocks you.
        </p>
      </form>
    </AuthShell>
  );
}
